<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import {
    IonList,
    IonLabel,
    IonIcon,
    IonTabs,
    IonItem,
    IonPage,
    IonHeader,
    IonToolbar,
    IonChip,
    IonTitle,
    IonContent,
    IonButtons,
    IonNote,
    IonProgressBar,
    IonNavLink,
    IonButton,
    IonTabBar,
    IonTabButton,
    IonItemGroup,
    IonItemDivider,
    IonBackButton,
    IonReorderGroup,
    IonSkeletonText,
    IonThumbnail,
    IonReorder,
    IonRouterOutlet,
    IonSpinner,
    IonListHeader,
    IonText,
    IonFabButton,
    IonFab,
    IonMenuButton,
} from "@ionic/vue";
import { modalController } from "@ionic/vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import WeekSelectorModal from "@/components/modals/WeekSelectorModal.vue";
import { calendarOutline, add, mapOutline } from "ionicons/icons";
import { format, startOfWeek, addDays } from "date-fns";
import { it, se } from "date-fns/locale";
import type { InterventionSchedule } from "@/types/planning";
import { usePlanningStore } from "@/stores/planning";
import { useCostumersStore } from "@/stores/costumers";
import { ICostumer } from "@/types/costumer";
import { IWeekModel, weekFactory } from "@/types/week";
import MapModal from "@/components/modals/MapModal.vue";
import { getMarkerIconUrl } from "@/types/intervention_frequency";
import InterventionScheduleForm from "@/components/modals/InterventionScheduleForm.vue";

const router = useRouter();
const authStore = useAuthStore();
const planningStore = usePlanningStore();
const costumersStore = useCostumersStore();

let initialWeek = undefined;
if (router.currentRoute.value.params.week) {
    initialWeek = new Date(router.currentRoute.value.params.week as string);
}

const selectedWeek = ref<IWeekModel>(weekFactory(initialWeek));
const agenda = ref<Record<string, (InterventionSchedule & { costumer: ICostumer })[]>>({});
const agendaIndexToOrderMap = ref<Record<number, number>>({});
const blockReorder = ref(false);
// Map flat list index (including dividers) to day key
const indexToDay = computed(() => {
    const arr: string[] = [];
    for (const [dayKey, schedules] of Object.entries(agenda.value)) {
        // divider element
        arr.push(dayKey);
        // each schedule item
        for (let i = 0; i < schedules.length; i++) {
            arr.push(dayKey);
        }
    }
    return arr;
});

const loadAgenda = (week: IWeekModel) => {
    // Group schedules by date
    const agenda: Record<string, (InterventionSchedule & { costumer: ICostumer })[]> = {};
    planningStore.getSchedulesbyWeek(selectedWeek.value).forEach((schedule) => {
        let dateKey: string;
        if (!schedule.scheduled_date) {
            dateKey = "no-date";
        } else {
            // Ensure the date is in YYYY-MM-DD format
            const scheduleDate = new Date(schedule.scheduled_date);
            dateKey = scheduleDate.toISOString().split("T")[0]; // YYYY-MM-DD format
        }

        if (!agenda[dateKey]) {
            agenda[dateKey] = [];
        }
        agenda[dateKey].push(schedule);
    });

    let index = 0;
    agendaIndexToOrderMap.value = {};
    for (const [date, schedules] of Object.entries(agenda)) {
        // Cycle in agenda (key, value)
        index++;
        schedules.forEach((schedule, _) => {
            agendaIndexToOrderMap.value[index] = schedule.order;
            index++;
        });
    }

    return agenda;
};

// const selectedWeekModel = computed(() => {
//     if (!selectedWeekStartDate.value) {
//         return null;
//     }
//     const start = startOfWeek(new Date(selectedWeekStartDate.value), { weekStartsOn: 1 }); // Monday start
//     const end = addDays(start, 6);
//     return {
//         start_day: format(start, "d", { locale: it }),
//         start_month: format(start, "MMMM", { locale: it }),
//         end_day: format(end, "d", { locale: it }),
//         end_month: format(end, "MMMM", { locale: it }),
//         year_label: format(start, "yyyy", { locale: it }),
//         start: start,
//         end: end,
//     };
// });
// const agendaSchedules = computed(() => {
//     // group schedules by day
//     const schedules = planningStore.getSchedules;
//     const groupedSchedules: Record<string, (InterventionSchedule & { cosumer?: ICostumer })[]> = {};
//     schedules.forEach((schedule) => {
//         if (!schedule.scheduled_date) {
//             console.error("Schedule has no scheduled date:", schedule);
//             return; // Skip if no scheduled date
//         }
//         const scheduleDate = new Date(schedule.scheduled_date);
//         const dayKey = format(scheduleDate, "yyyy-MM-dd");
//         if (!groupedSchedules[dayKey]) {
//             groupedSchedules[dayKey] = [];
//         }
//         groupedSchedules[dayKey].push({
//             ...schedule,
//             cosumer: costumersStore.getCustomerById(schedule.customer),
//         });
//     });
//     return groupedSchedules;
// });
// const schedulesLoading = computed(() => {
//     return planningStore.loading;
// });

const openWeekModal = async () => {
    const modal = await modalController.create({
        component: WeekSelectorModal,
        componentProps: {
            propWeek: selectedWeek.value,
        },
    });
    await modal.present();
    const selectResult = await modal.onDidDismiss();
    if (selectResult.role === "confirm") {
        selectedWeek.value = selectResult.data;
    }
};

const openAddModal = async () => {
    const modal = await modalController.create({
        component: InterventionScheduleForm,
        componentProps: {
            week: selectedWeek.value,
            order: 0,
        },
    });
    await modal.present();
    const scheduleData = await modal.onDidDismiss();
    if (scheduleData.role === "confirm") {
        const schedule = scheduleData.data;
        await planningStore.createSchedule(schedule);
        await planningStore.loadSchedules(selectedWeek.value.week);
        agenda.value = loadAgenda(selectedWeek.value);
    }
};

const onReorder = async (event: CustomEvent) => {
    const fromIndex = event.detail.from;
    const toIndex = event.detail.to;

    if (blockReorder.value) {
        // Already handling a reorder; complete UI and ignore duplicate
        event.detail.complete();
        return;
    }

    // Detect cross-day move using the flat index -> day map.
    const fromDay = indexToDay.value[fromIndex - 1];
    const toDay = indexToDay.value[toIndex - 1] ?? indexToDay.value[indexToDay.value.length - 1];

    console.log("Reorder fromIndex:", fromIndex, "toIndex:", toIndex, "fromDay:", fromDay, "toDay:", toDay);

    if (fromIndex === toIndex && fromDay === toDay) {
        event.detail.complete();
        return;
    }

    blockReorder.value = true;
    const response = await planningStore.reorderSchedule(
        selectedWeek.value,
        toDay,
        agendaIndexToOrderMap.value[fromIndex],
        agendaIndexToOrderMap.value[toIndex],
    );
    if (!response || response.status !== 200) {
        // Reorder failed, reset the reorder UI
        event.detail.complete();
        blockReorder.value = false;
        return;
    }
    agenda.value = loadAgenda(selectedWeek.value);
    blockReorder.value = false;

    event.detail.complete();
};

const formatDate = (date: string) => {
    const display = format(new Date(date), "EEEE d MMM - dd/MM/yyyy", { locale: it });
    // Capitalize the first letter
    return display.charAt(0).toUpperCase() + display.slice(1);
};

const showDayMap = (day: string) => {
    const markers = agenda.value[day].map((s) => ({
        position: {
            lat: Number(s.costumer.latitude),
            lng: Number(s.costumer.longitude),
        },
        title: s.costumer.business_name,
        infoWindowHTML: `<p>${s.costumer.business_name}<br/>${s.costumer.address}</p>`,
        icon: getMarkerIconUrl(s.costumer.expiration_state!),
    }));
    modalController
        .create({
            component: MapModal,
            componentProps: {
                markersData: markers,
                disableInteractions: false,
            },
        })
        .then((modal) => {
            modal.present();
        });
};

const goToCalendar = async () => {
    router.push({ name: "calendar" });
};

watch(selectedWeek, (newWeek, oldWeek) => {
    if (newWeek && newWeek.week !== oldWeek?.week) {
        // console.log("Selected week changed:", newWeek);
        // console.log("Navigating to week:", newWeek.week);
        router.push({ name: "planning-by-week", params: { week: newWeek.week }, replace: true });
        // planningStore.loadSchedules(newWeek.week);
        // agenda.value = loadAgenda(newWeek);
    }
});

// watch(
//     () => costumersStore.isLoading,
//     (newVal, oldValue) => {
//         if (!newVal) {
//             planningStore.loadSchedules(selectedWeek.value.week);
//             agenda.value = loadAgenda(selectedWeek.value);
//         }
//     },
//     {immediate: true}
// );

const editSchedule = async (schedule: InterventionSchedule & { costumer: ICostumer }) => {
    const modal = await modalController.create({
        component: InterventionScheduleForm,
        componentProps: {
            interventionSchedule: schedule,
            week: selectedWeek.value,
            order: schedule.order,
        },
    });
    await modal.present();
    const scheduleData = await modal.onDidDismiss();
    if (scheduleData.role === "confirm") {
        const updatedSchedule = scheduleData.data;
        await planningStore.updateSchedule(updatedSchedule);
        await planningStore.loadSchedules(selectedWeek.value.week);
        agenda.value = loadAgenda(selectedWeek.value);
    } else if (scheduleData.role === "delete") {
        await planningStore.deleteSchedule(schedule.id);
        await planningStore.loadSchedules(selectedWeek.value.week);
        agenda.value = loadAgenda(selectedWeek.value);
    }
};

onMounted(() => {
    planningStore.loadSchedules(selectedWeek.value.week).then(() => {
        agenda.value = loadAgenda(selectedWeek.value);
    });
});
</script>
<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-menu-button></ion-menu-button>
                </ion-buttons>
                <ion-title>Pianificazione</ion-title>
                <ion-buttons v-show="!planningStore.isLoading" slot="end" class="">
                    <ion-buttons slot="end" class="ion-padding-end">
                        <ion-button color="dark" v-if="selectedWeek" @click="openWeekModal">
                            <div>
                                <small style="display: block; font-size: 10px; margin-bottom: 3px">Settimana</small>
                                <div style="display: flex; gap: 4px; align-items: end">
                                    <span style="display: block; font-size: 18px">{{ selectedWeek.startDay }}</span>
                                    <small style="display: block; font-size: 8px; opacity: 0.7">
                                        {{ selectedWeek.startMonth }}
                                    </small>
                                    <span style="display: block">-</span>
                                    <span style="display: block; font-size: 18px">{{ selectedWeek.endDay }}</span>
                                    <small style="display: block; font-size: 8px; opacity: 0.7">
                                        {{ selectedWeek.endMonth }}
                                    </small>
                                </div>
                            </div>
                        </ion-button>
                    </ion-buttons>
                    <!-- <ion-button color="dark" @click="$router.push({ name: 'planning-deadlines' })">
                        <div style="display: flex; flex-direction: column; align-items: center">
                            <img
                                src="@/assets/icons/deadline.svg"
                                alt="Deadlines"
                                style="display: block; width: 20px; height: 20px" />
                            <span style="display: block; font-size: 10px; display: block; margin-top: 2px">
                                Scadenze
                            </span>
                        </div>
                    </ion-button> -->
                </ion-buttons>
            </ion-toolbar>
            <div v-if="blockReorder" style="background-color: var(--ion-background-color)">
                <ion-progress-bar type="indeterminate"></ion-progress-bar>
            </div>
        </ion-header>
        <ion-content :fullscreen="true">
            <div>
                <ion-list
                    v-if="!costumersStore.isLoading && !planningStore.isLoading && Object.keys(agenda).length > 0">
                    <ion-reorder-group
                        :disabled="blockReorder"
                        @ionReorderEnd="onReorder($event)"
                        style="margin-top: 0">
                        <!-- !FIXME: Dont know why but the object entry is stored all in `schedule_day` variable -->
                        <template v-for="(schedule_day, schedules) in Object.entries(agenda)">
                            <ion-item-divider
                                :sticky="true"
                                class="ion-padding-vertical-half"
                                style="padding-inline: 0">
                                <ion-title color="dark">{{ formatDate(schedule_day[0]) }}</ion-title>
                                <ion-button
                                    fill="clear"
                                    size="small"
                                    style="position: absolute; right: 10px; top: 5px"
                                    @click="showDayMap(schedule_day[0])">
                                    <ion-icon :icon="mapOutline" />
                                </ion-button>
                            </ion-item-divider>
                            <ion-item
                                v-for="s in schedule_day[1]"
                                :key="s.id"
                                style="
                                    position: relative;
                                    --padding-start: 0;
                                    --border-color: var(--ion-color-step-250);
                                ">
                                <span
                                    style="
                                        position: absolute;
                                        display: flex;
                                        align-items: center;
                                        bottom: 4px;
                                        right: 4px;
                                        min-height: 16px;
                                        padding-top: 3px;
                                        padding-bottom: 2px;
                                        padding-left: 5px;
                                        padding-right: 5px;
                                        line-height: 1;
                                        border-radius: 5px;
                                    "
                                    :style="{
                                        backgroundColor: s.fixed
                                            ? 'var(--ion-color-step-800)'
                                            : 'var(--ion-color-step-200)',
                                        color: s.fixed ? 'var(--ion-item-background)' : 'var(--ion-color-step-800)',
                                    }">
                                    <span
                                        v-if="s.fixed"
                                        style="font-size: 8px; display: block; text-transform: uppercase">
                                        manuale
                                    </span>
                                    <span v-else style="font-size: 8px; display: block; text-transform: uppercase">
                                        generato
                                    </span>
                                </span>
                                <ion-label
                                    @click="editSchedule(s)"
                                    style="
                                        padding-left: 16px;
                                        min-height: 91px;
                                        display: flex;
                                        flex-direction: column;
                                        justify-content: center;
                                        align-items: start;
                                        cursor: pointer;
                                    ">
                                    <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 5px">
                                        <h4
                                            style="
                                                padding: 0;
                                                margin: 0;
                                                font-size: 18px;
                                                font-weight: bold;
                                                text-decoration: underline;
                                            ">
                                            {{ s.label ? s.label : s.costumer.business_name ?? "Unknown" }}
                                        </h4>
                                        <div
                                            :class="`expiration-indicator ${s.costumer.expiration_state}`"
                                            style="display: block; padding: 2px 4px; margin-top: 6px">
                                            <span
                                                v-if="s.costumer.expiration_state"
                                                style="white-space: nowrap; font-size: 8px; display: block">
                                                {{ s.costumer.last_inspection_delta }} giorni fa
                                            </span>
                                        </div>
                                    </div>

                                    <p
                                        v-if="s.costumer.zone"
                                        style="
                                            margin-top: 0;
                                            margin-bottom: 5px;
                                            font-size: 14px;
                                            color: var(--ion-color-medium);
                                        ">
                                        {{ costumersStore.getZoneById(s.costumer.zone)?.name ?? "N/A" }}
                                    </p>
                                    <p
                                        style="
                                            margin-top: 0;
                                            margin-bottom: 5px;
                                            font-size: 14px;
                                            color: var(--ion-color-medium);
                                        ">
                                        {{ s.costumer.address }}
                                    </p>
                                </ion-label>
                                <div slot="end" style="display: flex; align-items: center; gap: 20px">
                                    <ion-reorder />
                                </div>
                            </ion-item>
                        </template>
                        <!-- <ion-item-divider :sticky="true" class="ion-padding-vertical-half" style="padding-inline: 0">
                            <ion-title color="dark">CIAO</ion-title>
                        </ion-item-divider>
                        <ion-item v-for="schedule in planningStore.getSchedules" :key="schedule.id">
                            <ion-icon :icon="playCircle"></ion-icon>
                            <ion-label>{{ costumersStore.getCustomerById(schedule.customer).business_name }}</ion-label>
                            <ion-reorder slot="end" />
                        </ion-item> -->
                    </ion-reorder-group>
                </ion-list>
                <ion-list v-if="costumersStore.isLoading || planningStore.isLoading">
                    <ion-list-header>
                        <ion-skeleton-text :animated="true" style="width: 80px"></ion-skeleton-text>
                    </ion-list-header>
                    <ion-item v-for="i in 10" :key="i">
                        <ion-thumbnail slot="start">
                            <ion-skeleton-text :animated="true"></ion-skeleton-text>
                        </ion-thumbnail>
                        <ion-label>
                            <h3>
                                <ion-skeleton-text :animated="true" style="width: 80%"></ion-skeleton-text>
                            </h3>
                            <p>
                                <ion-skeleton-text :animated="true" style="width: 60%"></ion-skeleton-text>
                            </p>
                            <p>
                                <ion-skeleton-text :animated="true" style="width: 30%"></ion-skeleton-text>
                            </p>
                        </ion-label>
                    </ion-item>
                </ion-list>
                <div
                    v-if="Object.keys(agenda).length === 0 && !costumersStore.isLoading && !planningStore.isLoading"
                    class="ion-padding"
                    style="margin-top: 50px; max-width: 480px; margin-left: auto; margin-right: auto">
                    <ion-text class="ion-margin-bottom" style="display: block">
                        <h2>Nessun intervento pianificato per questa settimana</h2>
                        <p>Per favore, seleziona un'altra settimana o aggiungi nuovi interventi.</p>
                    </ion-text>
                    <ion-button
                        expand="block"
                        fill="outline"
                        @click="openWeekModal"
                        style="max-width: 300px; margin-left: auto">
                        Seleziona un'altra settimana
                    </ion-button>
                </div>
            </div>
            <ion-fab slot="fixed" vertical="bottom" horizontal="end">
                <ion-fab-button @click="openAddModal">
                    <ion-icon :icon="add"></ion-icon>
                </ion-fab-button>
            </ion-fab>
        </ion-content>
    </ion-page>
</template>
